import { Box, Button, Container, Group, Loader, Stack, Text, Title, Alert } from "@mantine/core";
import { useParams, Link } from "react-router-dom";
import { useTeamMatchesQuery, MatchType } from "generated/graphql";
import UserMatchFeed, { MatchItem } from "components/profile/UserMatchFeed";

/**
 * TeamMatches
 *
 * Team-scoped matches page. Route: /teams/:teamId/matches
 * - Shows a back button that returns to the team profile
 * - Uses the TeamMatches GraphQL query to fetch team match history
 */
export default function TeamMatches() {
  const { teamId } = useParams<{ teamId: string }>();

  const { data, loading, error } = useTeamMatchesQuery({
    variables: {
      teamId: teamId ?? "",
      limit: 20,
      offset: 0,
    },
    skip: !teamId,
  });

  const backHref = `/teams/${teamId ?? ""}`;

  // Transform API data to MatchItem format
  const items: MatchItem[] = (data?.teamMatches ?? []).map((match) => {
    const isCompleted = match.status === "Completed";
    const homeTeam = match.gameSeries?.homeTeam?.name ?? "Team A";
    const awayTeam = match.gameSeries?.awayTeam?.name ?? "Team B";

    // Determine opponent based on which team this is
    const isHomeTeam = match.gameSeries?.homeTeamId === teamId;
    const opponent = isHomeTeam ? awayTeam : homeTeam;

    // Determine result (W/L/D) for completed matches
    let result: "W" | "L" | "D" | undefined = undefined;
    if (isCompleted && match.winningTeam) {
      if (isHomeTeam) {
        result = match.winningTeam === "TEAM_A" ? "W" : "L";
      } else {
        result = match.winningTeam === "TEAM_B" ? "W" : "L";
      }
    } else if (isCompleted && match.homeTeamScore === match.awayTeamScore) {
      result = "D";
    }

    return {
      id: match.id,
      opponent,
      scheduledAt: match.scheduledTime ?? match.startTime ?? new Date().toISOString(),
      result,
      score: match.homeTeamScore !== null && match.awayTeamScore !== null
        ? `${match.homeTeamScore}-${match.awayTeamScore}`
        : undefined,
      tournament: match.tier?.tierName ?? match.matchType ?? "Match",
      type: isCompleted ? "past" : "upcoming",
    };
  });

  return (
    <Container size="lg" p="md">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={3}>Team Matches</Title>
          <Button component={Link} to={backHref} variant="light">
            Back to Team
          </Button>
        </Group>

        {loading && (
          <Group justify="center" p="xl">
            <Loader size="lg" />
            <Text>Loading matches...</Text>
          </Group>
        )}

        {error && (
          <Alert color="red" title="Error loading matches">
            {error.message}
          </Alert>
        )}

        {!loading && !error && (
          <Box>
            <UserMatchFeed
              title="Team Match Feed"
              items={items}
              emptyMessage="No matches found for this team."
            />
          </Box>
        )}
      </Stack>
    </Container>
  );
}
